import pluralize from 'pluralize';
import { memo, useCallback, useMemo } from 'react';

import { activeUploadIdsSelector, completedUploadIdsSelector, failedUploadIdsSelector } from '@air/redux-uploader';

import { MainSideNavOverlayTraySectionHeader } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTraySectionHeader';
import { MainSideNavOverlayTrayVirtualizedList } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTrayVirtualizedList';
import {
  buildWorkspaceUploadsVirtualRows,
  estimateWorkspaceUploadsVirtualRowSize,
  getWorkspaceUploadsVirtualRowKey,
  type WorkspaceUploadsVirtualRow,
} from '~/components/v2/components/workspace/workspace-uploads/buildWorkspaceUploadsVirtualRows';
import { WorkspaceUploadsCompletedListItem } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsCompletedListItem';
import { WorkspaceUploadsFailedListItem } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsFailedListItem';
import { WorkspaceUploadsInProgressListItem } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsInProgressListItem';
import { WorkspaceUploadsRetryAllButton } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsRetryAllButton';
import { useAirSelector } from '~/utils/ReduxUtils';

export type WorkspaceUploadsVirtualizedListProps = {
  isOnline: boolean;
};

export const WorkspaceUploadsVirtualizedList = memo(({ isOnline }: WorkspaceUploadsVirtualizedListProps) => {
  const inProgressIds = useAirSelector(activeUploadIdsSelector);
  const failedIds = useAirSelector(failedUploadIdsSelector);
  const completedIds = useAirSelector(completedUploadIdsSelector);
  const rows = useMemo(
    () =>
      buildWorkspaceUploadsVirtualRows({
        completedIds,
        failedIds,
        inProgressIds,
      }),
    [completedIds, failedIds, inProgressIds],
  );

  const renderRow = useCallback(
    (row: WorkspaceUploadsVirtualRow) => {
      switch (row.type) {
        case 'inProgress':
          return <WorkspaceUploadsInProgressListItem isOnline={isOnline} uploadId={row.uploadId} />;
        case 'failedHeader':
          return (
            <MainSideNavOverlayTraySectionHeader
              className={row.withSectionGap ? 'pt-2' : undefined}
              headerSuffix={<WorkspaceUploadsRetryAllButton />}
              headerTestId="WORKSPACE_UPLOADS_FAILED_HEADER"
              title={pluralize('error', row.count, true)}
            />
          );
        case 'failed':
          return <WorkspaceUploadsFailedListItem uploadId={row.uploadId} />;
        case 'completedHeader':
          return (
            <MainSideNavOverlayTraySectionHeader
              className={row.withSectionGap ? 'pt-2' : undefined}
              headerTestId="WORKSPACE_UPLOADS_COMPLETED_HEADER"
              title={`${row.count} completed`}
            />
          );
        case 'completed':
          return <WorkspaceUploadsCompletedListItem uploadId={row.uploadId} />;
      }
    },
    [isOnline],
  );

  return (
    <MainSideNavOverlayTrayVirtualizedList
      estimateSize={estimateWorkspaceUploadsVirtualRowSize}
      getRowKey={getWorkspaceUploadsVirtualRowKey}
      renderRow={renderRow}
      rows={rows}
      testId="WORKSPACE_UPLOADS_LIST"
    />
  );
});

WorkspaceUploadsVirtualizedList.displayName = 'WorkspaceUploadsVirtualizedList';
